import React, { useState, useContext, useCallback, useRef } from 'react'
import { Box } from '@mui/material'
import { SwalContext } from 'genesis-suite/components'
import { Trash, AddUser, UserMinus } from 'genesis-suite/icons'
import TableAction from '../buttons/TableAction'
import { userService } from '../../lib/services'
import AddUserModal from '../manage-users/AddUserModal'
import AgDataTable from '../ag-data-table/DataTable'
import { isEmpty } from 'lodash'
import { SortObject } from 'genesis-suite/types/utilTypes'
import { SortMode } from 'genesis-suite/types/visualTypes'
import { ColDef, GetRowIdParams, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core'
interface SuperUser {
    userInfo: UserInfo
}
interface UserInfo {
    id?: string
    userId: string
    screenAlias?: string
    firstName?: string
    lastName?: string
    phone?: string
    email?: string
}
const tableColumns: ColDef[] = [
    {
        pinned: 'left',
        colId: 'checkbox',
        lockPosition: 'left',
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        resizable: false,
        width: 20,
        minWidth: 50,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
    },
    { headerName: 'Username', field: 'userId', flex: 1 },
    { headerName: 'Display Name', field: 'screenAlias', width: 150 },
    { headerName: 'First Name', field: 'firstName', width: 150 },
    { headerName: 'Last Name', field: 'lastName', width: 150 },
    { headerName: 'Phone', field: 'phone', width: 150 },
    { headerName: 'Email', field: 'email', flex: 1 },
]

const PAGE_SIZE = 100

export default function ManageCreators() {
    const [loading, setLoading] = useState(true)
    const [creators, setCreators] = useState<UserInfo[]>([])
    const [addUserModelOpen, setAddUserModalOpen] = useState(false)
    const [selectedUsers, setSelectedUsers] = useState<UserInfo[]>([])
    const [gridStatus, setGridStatus] = useState<string>('')
    const [gridAPI, setGridAPI] = useState<GridApi>(null)

    const { alert, confirm } = useContext(SwalContext)

    const dataTableRef = useRef(null)

    const errCallback = useCallback(
        err => {
            alert(err.Message || err.message, { type: 'error' })
            setLoading(false)
        },
        [alert]
    )

    const loadUsersList = useCallback(async (pageNumber, searchText = null) => {
        try {
            const creatorsRes = await userService.getSuperUsersV2(pageNumber, PAGE_SIZE, searchText)
            const processedCreators = creatorsRes?.users?.map((userObj, i) => ({
                ...userObj.userInfo,
                id: userObj.userInfo.userId,
            }))

            setCreators(processedCreators)

            return { creators: processedCreators, totalCount: creatorsRes?.totalCount }
        } catch (error: any) {
            if (error?.__CANCEL__) return
            errCallback(error)
        }
    }, [])

    const createDatasource = (searchText = null) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                try {
                    const { sortModel, endRow } = params.request
                    const sort: SortObject[] = []
                    if (!isEmpty(sortModel)) {
                        sortModel.forEach(so => {
                            const sortObj: SortObject = {
                                Name: so.colId === 'role' ? 'permissionId' : so.colId,
                                SortMode: so.sort === 'asc' ? SortMode.Ascending : SortMode.Descending,
                            }
                            sort.push(sortObj)
                        })
                    }
                    const pageNumber = Math.ceil(endRow / PAGE_SIZE)
                    const response = await loadUsersList(pageNumber, searchText)
                    if (Number(response?.totalCount) === 0) params.api.showNoRowsOverlay()
                    const { creators, totalCount } = response
                    const totalPages = Math.ceil(totalCount / PAGE_SIZE)
                    setGridStatus(
                        `Showing pages ${pageNumber} of ${totalPages}, 1 - ${endRow >= totalCount ? totalCount : endRow
                        } of ${totalCount} users`
                    )
                    params.success({ rowData: creators ?? [] })
                } catch (error) {
                    console.error(error)
                    params.fail()
                }
            },
        }
    }

    const handleUserCheckbox = useCallback(event => {
        const id = event?.data?.id
        if (event.node.isSelected()) {
            setSelectedUsers(prevIds => [...prevIds, { userId: id }])
        } else {
            setSelectedUsers(prevIds => prevIds.filter(prevId => prevId !== id))
        }
    }, [])

    const getRowId = useCallback((params: GetRowIdParams) => params.data.id, [])

    const handleAddSuperUser = (superUser: any) => {
        try {
            userService.saveUser({ ...superUser, isCoreSuperUser: true }).finally(() => {
                setAddUserModalOpen(false)
                gridAPI?.refreshServerSide()
                gridAPI?.refreshCells()
            })
        } catch (err) {
            alert(err.Message || err.message, { type: 'error' })
        }
    }

    const handleBulkOperation = async (promises: Promise<any>[]) => {
        setLoading(true)
        Promise.allSettled(promises)
            .then(async results => {
                setSelectedUsers([])

                if (results.every(({ status }) => status === 'fulfilled'))
                    await alert('User(s) saved successfully!', { type: 'success' })
                else await alert('Some users could not be saved successfully', { type: 'error' })

                gridAPI?.refreshServerSide()
                gridAPI?.deselectAll()
                gridAPI?.refreshCells()
            })
            .catch(err => {
                alert(err.Message || err.message, { type: 'error' })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const confirmAction = async () => {
        return await confirm(
            `Removing a creator will leave all of that creator's clouds 
        without other collaborators unreachable. Are you sure you want to continue?`,
            {
                type: 'warning',
                confirmButtonProps: { text: 'Proceed' },
            }
        )
    }

    const handleDeleteSuperUsers = async () => {
        const response = await confirmAction()
        if (response.dismiss) return

        const promises = selectedUsers.map(user => userService.deleteUser('', user?.userId))

        return handleBulkOperation(promises)
    }

    const handleDemoteSuperUsers = async () => {
        const response = await confirmAction()
        if (response.dismiss) return

        const promises = creators
            .filter(c => selectedUsers.some(s => s?.userId === c?.userId))
            ?.map(user => userService.saveUser({ ...user, isCoreSuperUser: false }))

        return handleBulkOperation(promises)
    }

    const onGridReady = useCallback((params: { api: GridApi }) => {
        const { api } = params
        setGridAPI(api)
        api.setGridOption('serverSideDatasource', createDatasource())
    }, [])

    const handleSearch = value => {
        gridAPI.setGridOption('serverSideDatasource', createDatasource(value))
    }

    const AddButton = () => (
        <TableAction title="Add Creator" onClick={() => setAddUserModalOpen(true)}>
            <AddUser />
        </TableAction>
    )
    const DemoteButton = () => (
        <TableAction title="Demote Creator(s)" onClick={handleDemoteSuperUsers} disabled={selectedUsers.length === 0}>
            <UserMinus />
        </TableAction>
    )
    const DeleteButton = () => (
        <TableAction title="Delete Creator(s)" onClick={handleDeleteSuperUsers} disabled={selectedUsers.length === 0}>
            <Trash />
        </TableAction>
    )

    return (
        <Box sx={{ height: '100%', p: 1 }}>
            <AgDataTable
                ref={dataTableRef}
                onGridReady={onGridReady}
                columns={tableColumns}
                extraActions={[<AddButton key="add" />, <DemoteButton key="demote" />, <DeleteButton key="delete" />]}
                title="Manage Creators"
                onRowSelected={handleUserCheckbox}
                gridStatus={gridStatus}
                handleSearch={handleSearch}
                getRowId={getRowId}
            />
            <AddUserModal
                requireContext={false}
                users={creators}
                open={addUserModelOpen}
                onSubmit={handleAddSuperUser}
                onClose={() => setAddUserModalOpen(false)}
            />
        </Box>
    )
}
